import { useMemo, useState } from "react";
import EditModal from "../ui/EditModal";
import { formatDate } from "../../utils/dateUtils";

const VaccinationTable = ({ data, onUpdate, onDelete }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortDir, setSortDir] = useState("desc");
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sorted = useMemo(() => {
    return data.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortDir]);

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const toggleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      await onUpdate(editingItem.id, formData);
      setIsModalOpen(false);
      setEditingItem(null);
    } catch {
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this vaccination record?")) {
      try {
        await onDelete(id);
      } catch {
        alert("Failed to delete entry");
      }
    }
  };

  // Highlight upcoming vaccines
  const getNextDueBadge = (nextDueDate) => {
    if (!nextDueDate) return null;
    const due = new Date(nextDueDate);
    const now = new Date();
    const daysUntil = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return <span className="badge bg-danger">Overdue</span>;
    if (daysUntil <= 30) return <span className="badge bg-warning text-dark">Due soon</span>;
    return <span className="text-muted">{due.toLocaleDateString()}</span>;
  };

  const editFields = [
    { name: "date", label: "Date Given", type: "datetime-local", required: true },
    { name: "vaccineName", label: "Vaccine Name", type: "text", required: true },
    { name: "dose", label: "Dose #", type: "text" },
    { name: "nextDueDate", label: "Next Due Date", type: "date" },
    { name: "notes", label: "Notes", type: "text" },
  ];

  return (
    <>
      <div className="alert alert-info mb-3">
        <strong>Total Vaccinations:</strong> {data.length} recorded
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ cursor: "pointer" }} onClick={toggleSort}>
                Date Given {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th>Vaccine</th>
              <th>Dose</th>
              <th>Next Due</th>
              <th>Notes</th>
              <th style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.date)}</td>
                <td><strong>{row.vaccineName}</strong></td>
                <td>{row.dose || <span className="text-muted">-</span>}</td>
                <td>{getNextDueBadge(row.nextDueDate) || <span className="text-muted">-</span>}</td>
                <td>{row.notes || <span className="text-muted">-</span>}</td>
                <td>
                  <button
                    className="btn btn-xs btn-warning me-1"
                    title="Edit"
                    onClick={() => handleEdit(row)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✎
                  </button>
                  <button
                    className="btn btn-xs btn-danger"
                    title="Delete"
                    onClick={() => handleDelete(row.id)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav>
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.max(p - 1, 1))}>Previous</button>
            </li>
            {[...Array(pageCount)].map((_, idx) => (
              <li key={idx + 1} className={`page-item ${idx + 1 === page ? "active" : ""}`}>
                <button className="page-link" onClick={() => setPage(idx + 1)}>{idx + 1}</button>
              </li>
            ))}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.min(p + 1, pageCount))}>Next</button>
            </li>
          </ul>
        </nav>
      )}

      <EditModal
        isOpen={isModalOpen}
        item={editingItem}
        title="Edit Vaccination"
        fields={editFields}
        onSave={handleSave}
        onCancel={() => { setIsModalOpen(false); setEditingItem(null); }}
        isLoading={isSaving}
      />
    </>
  );
};

export default VaccinationTable;
